import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Mail, Lock, Phone, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CreateUser() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'lawyer',
    department: '',
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/create-user`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/employees');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'فشل إنشاء المستخدم');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand py-12" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg mb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
            <ArrowRight className="w-6 h-6 text-white" />
          </button>
          <Logo size="md" />
          <h1 className="text-2xl font-heading font-bold text-white">إنشاء مستخدم جديد</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-xl p-8">
          {success ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-heading font-bold text-primary mb-2">
                تم إنشاء المستخدم بنجاح!
              </h2>
              <p className="text-gray-600 font-body">جاري التحويل إلى صفحة الموظفين...</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-heading font-bold text-primary mb-2">
                  إضافة موظف أو عميل جديد
                </h2>
                <p className="text-sm text-gray-600 font-body">
                  املأ البيانات أدناه لإنشاء حساب جديد
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-3" data-testid="error-message">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-sm text-red-700 font-body">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    الاسم الكامل <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="create-user-name"
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                      placeholder="أدخل الاسم الكامل"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    البريد الإلكتروني <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="create-user-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                      placeholder="example@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    كلمة المرور <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="create-user-password"
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    رقم الهاتف
                  </label>
                  <div className="relative">
                    <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="create-user-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                      placeholder="05xxxxxxxx"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    الدور الوظيفي <span className="text-red-500">*</span>
                  </label>
                  <select
                    data-testid="create-user-role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                  >
                    <option value="lawyer">محامي</option>
                    <option value="accountant">محاسب</option>
                    <option value="staff">موظف إداري</option>
                    <option value="client">عميل</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    القسم/التخصص
                  </label>
                  <input
                    data-testid="create-user-department"
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                    placeholder="مثال: قانون جنائي، قانون تجاري، المحاسبة"
                  />
                </div>

                <button
                  data-testid="create-user-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold text-white py-3 rounded-md hover:bg-gold-dark transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 font-heading font-medium"
                >
                  {loading ? 'جاري الإنشاء...' : 'إنشاء الحساب'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
